import React, { useState } from "react";
import { IconButton, Box, Typography, Stack } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "components/common/DTextField";
import {
    Visibility,
    VisibilityOff,
    // CheckCircleOutline,
    CancelOutlined,
    CheckCircle as CheckCircleOutline
} from "@mui/icons-material";

// Standard EF / ASP.NET Core Identity Password Rules
const checkRules = (password = "") => [
    {
        label: "At least 8 characters",
        met: password.length >= 8
    },
    {
        label: "At least one uppercase letter (A-Z)",
        met: /[A-Z]/.test(password)
    },
    {
        label: "At least one lowercase letter (a-z)",
        met: /[a-z]/.test(password)
    },
    {
        label: "At least one digit (0-9)",
        met: /[0-9]/.test(password)
    },
    {
        label: "At least one special character (@$!%*?&#...)",
        met: /[^a-zA-Z0-9]/.test(password)
    }
];

export default function PasswordFieldRenderer({
    field,
    form,
    setForm,
    errors,
    showValidation,
    showRequirements = true
}) {
    const [showPassword, setShowPassword] = useState(false);
    const passwordValue = form[field.name] ?? "";

    const onChange = (event) =>
        setForm((prev) => ({
            ...prev,
            [field.name]: event.target.value
        }));

    const handleClickShowPassword = () => setShowPassword((prev) => !prev);

    const rules = checkRules(passwordValue);

    return (
        <Box sx={{ width: "100%", my: 1 }}>
            <TextField
                fullWidth
                margin="normal"
                type={showPassword ? "text" : "password"}
                label={field.label}
                value={passwordValue}
                required={field.required}
                error={showValidation && !!errors[field.name]}
                helperText={showValidation ? (errors[field.name] ?? "") : ""}
                onChange={onChange}
                disabled={field.disabled}
                autoComplete="new-password"
                sx={{
                    marginBottom: "unset",
                    marginTop: "unset",
                    margin: "4px"
                }}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        )
                    }
                }}
            />

            {/* Visual EF Password Requirements Checklist */}
            {showRequirements && (
                <Box
                    sx={{
                        p: 1.5,
                        mt: 0.5,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                        border: "1px solid",
                        borderColor: "divider"
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, display: "block", mb: 1 }}
                    >
                        Password Requirements:
                    </Typography>

                    <Stack spacing={0.5}>
                        {rules.map((rule, idx) => {
                            const isTouched = passwordValue.length > 0;
                            const isMet = rule.met;

                            let iconColor = "text.disabled";
                            if (isTouched) {
                                iconColor = isMet
                                    ? "success.main"
                                    : "error.main";
                            }

                            return (
                                <Stack
                                    key={idx}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    {isMet ? (
                                        <CheckCircleOutline
                                            sx={{
                                                fontSize: 16,
                                                color: iconColor
                                            }}
                                        />
                                    ) : (
                                        <CancelOutlined
                                            sx={{
                                                fontSize: 16,
                                                color: iconColor
                                            }}
                                        />
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: isMet
                                                ? "text.primary"
                                                : "text.secondary",
                                            fontWeight: isMet ? 500 : 400
                                        }}
                                    >
                                        {rule.label}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}
